<template>
  <o-dialog
    ref="dialog"
    :title="mode === 'makeups' ? $t('components.courseRun.actions.selectAvailableMakeupReservations.title') : $t('components.courseRun.actions.selectAvailableReservations.title')"
    :info="mode === 'makeups' ? $t('components.courseRun.actions.selectAvailableMakeupReservations.info') : $t('components.courseRun.actions.selectAvailableReservations.info')"
    :options="dialogOptions"
    :loading="loading"
    :portal="portal"
    :valid="validate"
    size="lg"
  >
    <div class="pt-4 text-left">
      <o-form ref="form">
        <!-- View All Toggle -->
        <o-field-group
          :label="mode === 'makeups' ? $t('components.courseRun.actions.selectAvailableMakeupReservations.viewAllLabel') : $t('components.courseRun.actions.selectAvailableReservations.viewAllLabel')"
          :info="mode === 'makeups' ? $t('components.courseRun.actions.selectAvailableMakeupReservations.viewAllInfo') : $t('components.courseRun.actions.selectAvailableReservations.viewAllInfo')"
          col
          reverse
          class="mb-4 block"
        >
          <toggle-field v-model="viewAll" />
        </o-field-group>

        <!-- Service Selection -->
        <o-field-group
          v-if="!viewAll"
          :label="mode === 'makeups' ? $t('components.courseRun.actions.selectAvailableMakeupReservations.serviceLabel') : $t('components.courseRun.actions.selectAvailableReservations.serviceLabel')"
          :name="mode === 'makeups' ? $t('components.courseRun.actions.selectAvailableMakeupReservations.serviceLabel') : $t('components.courseRun.actions.selectAvailableReservations.serviceLabel')"
          :info="mode === 'makeups' ? $t('components.courseRun.actions.selectAvailableMakeupReservations.serviceInfo') : $t('components.courseRun.actions.selectAvailableReservations.serviceInfo')"
          class="mb-4 block"
        >
          <service-select
            v-model="service"
            type="group"
            class="w-full bg-white dark:bg-gray-900"
          />
        </o-field-group>

        <!-- Date Selection -->
        <o-field-group
          v-if="viewAll || service"
          :label="mode === 'makeups' ? $t('components.courseRun.actions.selectAvailableMakeupReservations.datesLabel') : $t('components.courseRun.actions.selectAvailableReservations.datesLabel')"
          :name="mode === 'makeups' ? $t('components.courseRun.actions.selectAvailableMakeupReservations.datesLabel') : $t('components.courseRun.actions.selectAvailableReservations.datesLabel')"
          :info="mode === 'makeups' ? $t('components.courseRun.actions.selectAvailableMakeupReservations.datesInfo') : $t('components.courseRun.actions.selectAvailableReservations.datesInfo')"
          class="mb-4 block"
        >
          <week-swiper v-model="selectedDates" :data-dates="dataDates" @week-change="handleWeekChange" />
        </o-field-group>

        <!-- Results -->
        <o-field-group
          v-if="canShowResults"
          :label="mode === 'makeups' ? $t('components.courseRun.actions.selectAvailableMakeupReservations.resultsLabel') : $t('components.courseRun.actions.selectAvailableReservations.resultsLabel')"
          :name="mode === 'makeups' ? $t('components.courseRun.actions.selectAvailableMakeupReservations.resultsLabel') : $t('components.courseRun.actions.selectAvailableReservations.resultsLabel')"
          class="mb-4 block"
        >
          <div class="block -mx-4 border-t border-b h-80 bg-gray-100 dark:bg-gray-900 dark:border-gray-800 overflow-hidden">
            <div v-if="loadingReservations" class="flex items-center justify-center h-full">
              <o-loader />
            </div>
            <checkbox-group-field
              v-else-if="reservationOptions.length"
              v-model="selected"
              :options="reservationOptions"
              search
              class="h-80"
            />
            <p v-else class="text-sm opacity-70 text-center p-5">
              {{ mode === 'makeups' ? $t('components.courseRun.actions.selectAvailableMakeupReservations.noResults') : $t('components.courseRun.actions.selectAvailableReservations.noResults') }}
            </p>
          </div>
        </o-field-group>
      </o-form>
    </div>
  </o-dialog>
</template>

<script>
import ServiceSelect from '@/components/service/elements/Select'
import WeekSwiper from '@/components/courseRun/elements/WeekSwiper'

import {
  CheckboxGroupField,
  ToggleField
} from '@/components/fields'

export default {
  components: {
    ServiceSelect,
    WeekSwiper,
    CheckboxGroupField,
    ToggleField
  },
  props: {
    portal: {
      type: String,
      default: undefined
    },
    loading: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      default: 'sessions',
      validator: value => ['sessions', 'makeups'].includes(value)
    }
  },
  data () {
    return {
      courseRun: null,
      currentSelected: [],
      service: null,
      selectedDates: [],
      weekDates: [],
      selected: [],
      weekReservations: null,
      loadingReservations: false,
      viewAll: false
    }
  },
  computed: {
    dialogOptions () {
      const count = this.availableSelected.length
      const addText = count > 0
        ? this.mode === 'makeups'
          ? this.$t('components.courseRun.actions.selectAvailableMakeupReservations.addCount', { count })
          : this.$t('components.courseRun.actions.selectAvailableReservations.addCount', { count })
        : this.$t('general.add')

      return [
        { text: this.$t('general.cancel'), type: 'cancel' },
        { text: addText, type: 'confirm', callback: this.confirm }
      ]
    },
    currentSelectedIds () {
      return this.currentSelected
        .map(item => item?.idReservation || item?.reservation?.idReservation)
        .filter(Boolean)
    },
    dataDates () {
      if (!this.weekReservations) { return [] }
      const dates = new Set()
      this.weekReservations
        .filter(item => !this.currentSelectedIds.includes(item.reservation?.idReservation))
        .filter(item => !item.reservation?.isStatusCancelled)
        .forEach((item) => {
          const r = item.reservation
          const dateVal = r?.date?.integer ?? r?.date
          if (dateVal) { dates.add(String(dateVal)) }
        })
      return Array.from(dates)
    },
    filteredReservations () {
      if (!this.weekReservations || !this.selectedDates.length) { return [] }
      const selectedDate = this.selectedDates[0]
      return this.weekReservations.filter((item) => {
        const r = item.reservation
        const dateVal = r?.date?.integer ?? r?.date
        return String(dateVal) === selectedDate
      })
    },
    reservationOptions () {
      const items = this.filteredReservations

      if (!items) { return [] }

      return items
        .filter(item => !this.currentSelectedIds.includes(item.reservation?.idReservation))
        .filter(item => !item.reservation?.isStatusCancelled)
        .map((item) => {
          const reservation = item.reservation
          const warnings = []

          if (item.inRun) { warnings.push(this.$t('components.courseRun.actions.selectAvailableReservations.warnings.inRun')) }
          if (item.inMakeup) { warnings.push(this.$t('components.courseRun.actions.selectAvailableReservations.warnings.inMakeup')) }
          if (item.full) { warnings.push(this.$t('components.courseRun.actions.selectAvailableReservations.warnings.full')) }
          if (item.past) { warnings.push(this.$t('components.courseRun.actions.selectAvailableReservations.warnings.past')) }

          const infoText = reservation?.employee?.displayName || ''
          const warningText = warnings.length ? `⚠ ${warnings.join(', ')}` : ''
          const info = [infoText, warningText].filter(Boolean).join(' · ')

          return {
            value: item,
            text: reservation?.infoLabel,
            info,
            class: !item.available ? 'opacity-50 pointer-events-none' : ''
          }
        })
    },
    availableSelected () {
      return this.selected.filter(item => item?.available)
    },
    canFetch () {
      return (this.viewAll || this.service) && this.weekDates.length > 0
    },
    canShowResults () {
      return (this.viewAll || this.service) && this.selectedDates.length > 0
    }
  },
  watch: {
    service () {
      this.fetchReservations()
    },
    viewAll () {
      this.service = null
      this.fetchReservations()
    },
    weekDates: {
      handler () {
        this.fetchReservations()
      },
      deep: true
    }
  },
  methods: {
    open (courseRun, currentSelected = []) {
      this.courseRun = courseRun
      this.currentSelected = currentSelected
      this.selected = []
      this.service = null
      this.weekReservations = null
      this.selectedDates = []
      this.weekDates = []
      this.viewAll = false

      this.$refs.dialog.open()
    },
    confirm () {
      if (!this.validate()) { return false }

      const reservations = this.availableSelected.map(item => item.reservation)
      this.$emit('update', reservations)
      this.$refs.dialog.close()

      return true
    },
    validate () {
      return this.selected.length > 0 && this.selected.length === this.availableSelected.length
    },
    handleWeekChange (dates) {
      this.weekDates = dates
    },
    async fetchReservations () {
      if (!this.canFetch || !this.courseRun) { return }

      this.loadingReservations = true
      this.weekReservations = null

      try {
        const response = await this.$api.course.availableReservations(this.courseRun, {
          serviceIDs: this.viewAll ? undefined : [this.service.idService],
          dates: this.weekDates
        })
        this.weekReservations = response.items
      } catch (e) {
        console.error(e)
        this.$notification.error({ errors: e?.errors })
      }

      this.loadingReservations = false
    }
  }
}
</script>
